// frozen_string_literal: true

import { Controller } from "@hotwired/stimulus"
import { VehicleCardUpdater } from "./monitoring/vehicle_card_updater"
import { MapUpdater } from "./monitoring/map_updater"
import { MetricUpdater } from "./monitoring/metric_updater"

export default class extends Controller {
  static targets = ["lastUpdate", "status", "refreshButton"]
  static values = { 
    interval: { type: Number, default: 60000 },
    url: String
  }

  connect() {
    this.lastUpdateTime = new Date()
    this.isRefreshing = false
    
    this.initializeModules()
    
    this.startUpdateTimer()
    this.startRefreshTimer()
    
    this.updateTimeDisplay()
  }

  disconnect() {
    this.stopTimers()
    this.stopProgressAnimation()
  }

  initializeModules() {
    this.vehicleCardUpdater = new VehicleCardUpdater(this.application)
    this.mapUpdater = new MapUpdater()
    this.metricUpdater = new MetricUpdater()
  }

  startUpdateTimer() {
    this.updateTimerInterval = setInterval(() => {
      this.updateTimeDisplay()
    }, 10000)
  }

  startRefreshTimer() {
    this.refreshTimerInterval = setInterval(() => {
      this.performRefresh()
    }, this.intervalValue)
  }

  stopTimers() {
    if (this.updateTimerInterval) {
      clearInterval(this.updateTimerInterval)
      this.updateTimerInterval = null
    }
    if (this.refreshTimerInterval) {
      clearInterval(this.refreshTimerInterval)
      this.refreshTimerInterval = null
    }
  }

  updateTimeDisplay() {
    if (!this.hasLastUpdateTarget) return

    const now = new Date()
    const diffMs = now - this.lastUpdateTime
    const diffSeconds = Math.floor(diffMs / 1000)
    
    let timeText
    if (diffSeconds < 10) {
      timeText = 'agora'
    } else if (diffSeconds < 60) {
      const roundedSeconds = Math.ceil(diffSeconds / 10) * 10
      timeText = `${roundedSeconds}s`
    } else if (diffSeconds < 3600) {
      const minutes = Math.floor(diffSeconds / 60)
      timeText = `${minutes}min`
    } else {
      const hours = Math.floor(diffSeconds / 3600)
      timeText = `${hours}h`
    }
    
    this.lastUpdateTarget.textContent = timeText
  }

  async performRefresh() {
    if (this.isRefreshing) return

    this.isRefreshing = true
    this.setRefreshingState(true)
    this.startProgressAnimation()

    try {
      const jsonUrl = this.urlValue.replace('/map', '/vehicles_data')
      
      const response = await fetch(`${jsonUrl}?refresh=true`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (response.ok) {
        const data = await response.json()
        this.updateMapContent(data)
        this.lastUpdateTime = new Date()
        this.updateTimeDisplay()
        this.setRefreshingState(false)
      } else {
        throw new Error(`HTTP ${response.status}`)
      }
    } catch (error) {
      this.setRefreshingState(false, true)
    } finally {
      this.isRefreshing = false
      this.stopProgressAnimation()
    }
  }

  manualRefresh(event) {
    event?.preventDefault()
    this.lastUpdateTime = new Date()
    this.updateTimeDisplay()
    this.performRefresh()
  }

  setRefreshingState(isRefreshing, hasError = false) {
    if (this.hasStatusTarget) {
      if (hasError) {
        this.statusTarget.innerHTML = `
          <div class="flex items-center text-red-500">
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
        `
      } else if (isRefreshing) {
        this.statusTarget.innerHTML = `<div></div>`
      } else {
        this.statusTarget.innerHTML = `
          <div class="flex items-center text-green-500">
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
          </div>
        `
      }
    }

    if (this.hasRefreshButtonTarget) {
      this.refreshButtonTarget.disabled = isRefreshing
      if (isRefreshing) {
        this.refreshButtonTarget.classList.add('opacity-50', 'cursor-not-allowed')
      } else {
        this.refreshButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
      }
    }
  }

  updateMapContent(data) {
    if (!data.vehicles || !Array.isArray(data.vehicles)) {
      return
    }

    // Aplicar dados de teste se necessário
    let vehicles = data.vehicles
    const testData = this.shouldUseTestData()
    if (testData) {
      vehicles = this.metricUpdater.applyTestTelemetryData(vehicles)
    }

    // Atualizar contadores de status
    this.updateStatusCounters(data.counts)
    
    // Atualizar cards dos veículos usando módulo especializado
    this.vehicleCardUpdater.updateVehicleCards(vehicles)
    
    // Atualizar mapa usando módulo especializado
    this.mapUpdater.updateMap(data.vehicles, data.counts, data.timestamp)
  }

  updateStatusCounters(counts) {
    if (!counts) return

    const ignitionIndicators = document.querySelectorAll('.ignition-indicators .indicator span')
    if (ignitionIndicators.length >= 3) {
      ignitionIndicators[0].textContent = counts.ignition_on || 0
      ignitionIndicators[1].textContent = counts.ignition_idle || 0  
      ignitionIndicators[2].textContent = counts.ignition_off || 0
    }

    const commIndicators = document.querySelectorAll('.communication-indicators .indicator span')
    if (commIndicators.length >= 2) {
      commIndicators[0].textContent = counts.comm_ok || 0
      commIndicators[1].textContent = counts.comm_fail || 0
    }
  }

  shouldUseTestData() {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('debug') === 'telemetry'
  }

  // Métodos para controlar a animação de progresso
  startProgressAnimation() {
    if (!this.hasRefreshButtonTarget) return

    const button = this.refreshButtonTarget
    const refreshText = button.querySelector('.refresh-text')
    const progressCircle = button.querySelector('.progress-circle')
    const progressRing = button.querySelector('.progress-ring')

    if (refreshText && progressCircle && progressRing) {
      refreshText.style.opacity = '0'
      progressCircle.style.opacity = '1'
      progressRing.style.strokeDashoffset = '50.27'
      
      let progress = 0
      const duration = 2000
      const steps = 100
      const stepTime = duration / steps
      const circumference = 50.27
      
      this.progressInterval = setInterval(() => {
        progress += 1
        if (progress >= 95) {
          clearInterval(this.progressInterval)
          return
        }
        
        const offset = circumference - (progress / 100) * circumference
        progressRing.style.strokeDashoffset = offset.toString()
      }, stepTime)
    }
  }

  stopProgressAnimation() {
    if (!this.hasRefreshButtonTarget) return

    const button = this.refreshButtonTarget
    const refreshText = button.querySelector('.refresh-text')
    const progressCircle = button.querySelector('.progress-circle')
    const progressRing = button.querySelector('.progress-ring')

    if (this.progressInterval) {
      clearInterval(this.progressInterval)
      this.progressInterval = null
    }

    if (refreshText && progressCircle && progressRing) {
      progressRing.style.strokeDashoffset = '0'
      
      setTimeout(() => {
        refreshText.style.opacity = '1'
        progressCircle.style.opacity = '0'
      }, 200)
    }
  }
}